<template>
    <div class="text-center">
        <v-menu v-model="isMenuOpen" transition="slide-y-transition" bottom right offset-y :close-on-content-click="false" content-class="myprovet-menu-card">
            <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                    <v-text-field v-bind="{ ...obj.schema }" :value="display" readonly> </v-text-field>
                </div>
            </template>

            <v-card class="myprovet-menu-card-inner-no-shadow" data-cy="navbar-client-menu" width="100%" height="200px">
                <v-card-title class="d-flex justify-center align-center">
                    <v-text-field
                        :value="searchBranchID"
                        type="number"
                        dense
                        solo
                        flat
                        :placeholder="$t('clientMenu.number')"
                        prepend-inner-icon="mdi-counter"
                        hide-details
                        height="40px"
                        background-color="#F4F7FA"
                        class="font-size-sm input-bg-color-gray dark-gray-color mr-2 navbar-number client-number-field"
                        style="border-radius: 8px"
                        clearable
                        :disabled="!userStore.hasPermissionTo('View client and branch number')"
                        @input="checkIntegerNumber"
                    ></v-text-field>

                    <v-text-field
                        v-model="searchBranchName"
                        dense
                        solo
                        flat
                        :placeholder="$t('clientMenu.name')"
                        prepend-inner-icon="mdi-rename-box"
                        hide-details
                        height="40px"
                        background-color="#F4F7FA"
                        class="font-size-sm input-bg-color-gray dark-gray-color mr-2 name-field"
                        style="border-radius: 8px"
                        clearable
                        @input="search"
                    ></v-text-field>

                    <v-text-field
                        v-model="searchBranchTaxpayerNumber"
                        dense
                        solo
                        flat
                        :placeholder="$t('clientMenu.vat')"
                        prepend-inner-icon="mdi-circle-multiple-outline"
                        hide-details
                        height="40px"
                        background-color="#F4F7FA"
                        class="font-size-sm input-bg-color-gray dark-gray-color nif-field"
                        style="border-radius: 8px"
                        clearable
                        @input="search"
                    ></v-text-field>
                </v-card-title>

                <v-card-text class="px-0" v-bind:class="{ 'myprovet-menu-card-scroll': isScrollable }">
                    <v-list flat>
                        <v-list-item-group>
                            <div ref="beginning"></div>

                            <template v-for="(item, index) in getGroupOfBranches">
                                <menu-item
                                    :key="index"
                                    :icon="item.children ? 'mdi-alpha-c' : getBranchIcon(item)"
                                    :parent="item.children ? true : false"
                                    :name="item.name"
                                    :orange="item.is_petfood && !item.children"
                                    :id="item.id"
                                    ref="branchMenuItems"
                                    :companyBranch="item.company_branch"
                                    :active="isActiveSelection(item)"
                                    @click="onBranchSelection(item)"
                                >
                                </menu-item>

                                <v-divider v-if="index < branches.length - 1" :key="`${index}-divider`" class="mx-4" style="border-color: rgba(196, 196, 196, 0.2)"></v-divider>

                                <template v-if="item.children">
                                    <div v-for="child in item.children" :key="child.client_number + '_child_' + child.company_branch">
                                        <menu-item class="pl-16" :icon="getBranchIcon(child)" :name="child.name" :orange="child.is_petfood && !child.children" :id="child.id" :companyBranch="child.company_branch" :active="isActiveSelection(child)" @click="onBranchSelection(child)"> </menu-item>

                                        <v-divider class="mx-4" style="border-color: rgba(196, 196, 196, 0.2)"></v-divider>
                                    </div>
                                </template>
                            </template>
                        </v-list-item-group>
                    </v-list>

                    <v-lazy v-if="!isLoading">
                        <div v-intersect="updatePagination"></div>
                    </v-lazy>

                    <div v-else class="text-center">
                        <v-progress-circular class="dark-blue-color" indeterminate></v-progress-circular>
                    </div>
                </v-card-text>

                <div class="navbar-clients-total-amount">
                    <span class="dark-blue-color" style="font-size: 0.75rem">{{ $t('clientMenu.resultsCount', { shown: branches.length, total: totalResults }) }}</span>
                </div>
            </v-card>
        </v-menu>
    </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useBranchStore } from '@/Stores/branch';
import { useUserStore } from '@/Stores/user';
import { axios } from '@/Services/Backend';
import { buildQueryObjectFromModel } from '@arentia/atools/QueryBuilder';
import MenuItem from './VAppClientSelectorItem.vue';

export default {
    props: ['type', 'value', 'obj'],

    components: {
        MenuItem,
    },

    data() {
        return {
            isMenuOpen: false,
            isScrollable: true,

            selectedBranch: null,
            menu: false,

            searchBranchID: null,
            searchBranchName: null,
            searchBranchTaxpayerNumber: null,

            branches: [],

            page: 1,
            lastPage: 1,
            isLoading: false,
            totalResults: null,

            _timer: null,
        };
    },

    async created() {
        this.getBranches();
    },

    watch: {
        isMenuOpen() {
            // We don't need to do anything when the menu opens, only when it closes.
            if (this.isMenuOpen) return;

            if (this.searchBranchID !== null || this.searchBranchName !== null || this.searchBranchTaxpayerNumber !== null) {
                // If the menu is closed and the client had a search, clear it.
                this.$nextTick(() => this.clearFilters());
            }

            // Scroll to the beginning of the list with a small delay to hide the change while the menu is closing.
            setTimeout(() => this.$refs.beginning.scrollIntoView(true), 200);
        },
    },

    computed: {
        ...mapStores(useBranchStore, useUserStore),

        display() {
            if (!this.value) return null;

            return typeof this.value === 'string' ? this.value : this.value?.name;
        },

        getGroupOfBranches() {
            if (this.branches.length === 0) return [];

            // If company A and B have an ID of e.g. '40' (they're different branches of the same company)
            //  we want them to be grouped together with the main branch being the root node and the other branches the children nodes.
            const seenBranch = new Map();
            const result = [];

            for (const branch of this.branches) {
                if (seenBranch.has(branch.client_number)) continue;

                // Find all of the branches of the company.
                const branches = this.branches.filter((c) => c.client_number === branch.client_number);

                if (branches.length === 1) {
                    // Only one company branch (i.e. the main branch)
                    result.push(branch);
                } else {
                    // Multiple branches (i.e. add the consolidated company entry and all of the branches inside)
                    result.push({
                        name: this.$t('clientMenu.branchesOf', { name: branch.main_company_branch.name }),
                        children: branches,
                    });
                }

                seenBranch.set(branch.client_number, true);
            }

            return result;
        },
    },

    methods: {
        emitInput(event) {
            this.$emit('input', event?.trim() ?? '');
        },

        getBranchIcon(branch) {
            if (!branch) return '';
            return branch.is_petfood ? 'mdi-alpha-p' : 'mdi-alpha-f';
        },

        isActiveSelection(item) {
            if (!item.client_number) return false; // @Note: Consolidated branches are never selected.

            return this.selectedBranch?.id === item.id;
        },

        onBranchSelection(branch) {
            if (!branch?.id) {
                return;
            }

            this.$emit('input', branch.id);
            this.selectedBranch = branch;
            this.isMenuOpen = false;
        },

        clearFilters() {
            this.searchBranchID = null;
            this.searchBranchName = null;
            this.searchBranchTaxpayerNumber = null;

            this.search();
        },

        getBranches() {
            this.isLoading = true;

            const schema = {
                id: { dataFieldOperator: 'contains' },
                name: { dataFieldOperator: 'keywords' },
                taxpayer_number: { dataFieldOperator: 'contains' },
            };

            const search = { id: this.searchBranchID, name: this.searchBranchName, taxpayer_number: this.searchBranchTaxpayerNumber };
            const query = JSON.stringify(buildQueryObjectFromModel(search, schema));

            return axios
                .get(`/api/branches/selector/search?page=${this.page}&queryObject=${query}`)
                .then((response) => {
                    this.isLoading = false;
                    this.page = response.data.meta.current_page;
                    this.lastPage = response.data.meta.last_page;
                    this.totalResults = response.data.meta.total;
                    this.branches = [...this.branches, ...response.data.data];
                })
                .catch((error) => {
                    this.isLoading = false;

                    console.log(error);
                });
        },

        checkIntegerNumber(value) {
            if (!value) return;

            value = parseInt(value);
            if (!Number.isInteger(value)) return;

            this.searchBranchID = value;
            this.search();
        },

        search() {
            this.branches = [];
            this.page = 1;
            this.lastPage = 1;

            this.isLoading = true;

            // Debounce the search - it will prevent errors when updating too quickly.
            clearTimeout(this._timer);

            this._timer = setTimeout(() => {
                this.getBranches();
            }, 300);
        },

        updatePagination() {
            if (this.isLoading || this.page >= this.lastPage) return;

            this.page += 1;
            this.getBranches();
        },
    },
};
</script>

<style scoped>
.navbar-clients-total-amount {
    float: right;
    padding-right: 16px;
    padding-top: 5px;
    padding-bottom: 5px;
}
</style>

<style>
.myprovet-menu-card-inner-no-shadow {
    box-shadow: none !important;
}
</style>
